import { useDeviceInfo, type OutboxRecord } from '@/offline'
import type { GuardContext } from '../model/scanGuard'
import { closeResult } from '../model/day'
import {
  useCurrentSessions,
  useDeliveryDrafts,
  useGateOutbox,
  useGateRoster,
  useUsedCodes,
  type GateRoster,
} from './hooks'
import type { GateSession } from './session'

/** Everything a gate screen needs, live. `ready` is false until IndexedDB has answered. */
export function useGate() {
  const roster = useGateRoster()
  const outbox = useGateOutbox()
  const sessions = useCurrentSessions()
  const info = useDeviceInfo()
  const drafts = useDeliveryDrafts()
  const used = useUsedCodes(outbox)
  return {
    ready: !!roster && !!outbox && sessions.loaded && !!drafts && info.loaded,
    roster,
    outbox: outbox ?? [],
    sessions: sessions.sessions,
    app: sessions.app,
    paper: sessions.paper,
    info,
    drafts: drafts ?? [],
    used,
  }
}

export function guardFor(
  session: GateSession,
  roster: GateRoster,
  used: { bound: Set<string>; voided: Set<string> },
  kind: 'BAG' | 'SEAL',
  inDraft: ReadonlySet<string> = new Set(),
  otherDrafts?: ReadonlyMap<string, string>,
): GuardContext {
  // V1: any roll the office issued to the village is live at once (an older roster without the
  // flag counts every village roll as issued).
  const byVillage = roster.config.v1
  return {
    kind,
    operatorId: session.operator_id,
    rolls: roster.rolls,
    byVillage,
    confirmedBatchIds: new Set(
      byVillage
        ? roster.rolls.filter((r) => r.issued !== false).map((r) => r.batch_id)
        : session.rolls.map((r) => r.batch_id),
    ),
    boundPayloads: used.bound,
    voidedPayloads: used.voided,
    inDraft,
    otherDrafts,
  }
}

/**
 * F4.1 "Her last day did not add up: the phone will not open a new day until the supervisor
 * clears it." From the roster (team[].blocked) or, until the next roster download, from the last
 * close_session result on this phone.
 */
export function isBlocked(
  operatorId: string,
  roster: GateRoster,
  sessions: readonly GateSession[],
  outbox: readonly OutboxRecord[],
): boolean {
  // V1: no evening count, so nothing ever blocks her next day.
  if (roster.config.v1) return false
  if (roster.team.find((m) => m.user_id === operatorId)?.blocked) return true
  const last = sessions
    .filter(
      (s) => s.operator_id === operatorId && s.status === 'CLOSED' && s.capture_mode === 'APP',
    )
    .sort((a, b) => ((a.closed_at ?? '') < (b.closed_at ?? '') ? 1 : -1))[0]
  if (!last) return false
  const res = closeResult(outbox, last.id)
  if (!res || res.balanced !== false) return false
  return (res.acked_at ?? 0) > (roster.downloadedAt ?? 0)
}
